import { createFileRoute } from '@tanstack/react-router';
import { useQuery, usePaginatedQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/lib/api';
import type { Id } from 'convex/_generated/dataModel';
import { maxNotfallscheinbeteiligungRate } from 'convex/lib/notfallscheinbeteiligung';
import { Button } from '@/components/ui/button';
import ErrorBoundary from '@/components/ui/error-boundary';
import { Badge } from '@/components/ui/badge';
import { Icons } from '@/components/ui/icons';
import * as React from 'react';
import { MessagesList } from '@/components/features/messages-list';
import { Conversation, ConversationContent, ConversationComposer } from '@/components/ai-elements/conversation';
import { Calendar, MapPin, Lock, MessageSquare, FileText, X, LayoutGrid, List, AlertTriangle, ChevronRight, ChevronDown, Eye, Clock, Star } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { shouldShowDiensttauschFormular } from '@/lib/provider-service-visibility';
import { FileUpload, FileUploadDropzone, FileUploadTrigger, FileUploadList, FileUploadItem, FileUploadItemPreview, FileUploadItemMetadata, FileUploadItemDelete, FileUploadItemProgress } from '@/components/ui/file-upload';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookingPaymentModal } from '@/components/features/booking-payment-modal';
import { InvoiceDownloadButton } from '@/components/features/invoice-download-button';
import { DocumentPreviewModal } from '@/components/features/document-preview-modal';
import { ProviderBankTransferInstructions } from '@/components/features/provider-bank-transfer-instructions';
import { RatingInput } from '@/components/features/ratings/RatingInput';
import { CompensationSummaryCard } from '@/components/features/provider/compensation-summary-card';
import { ServiceProgressStepper } from '@/components/features/provider/service-progress-stepper';
import { ActiveApplicationsSection } from '@/components/features/provider/active-applications-section';
import { ServiceDetailsCard } from '@/components/features/provider/service-details-card';
import { MinimizedChatIndicator } from '@/components/features/provider/minimized-chat-indicator';
import { ConfirmedSubstituteCard } from '@/components/features/provider/confirmed-substitute-card';
import { PageHero } from '@/components/features/provider/page-hero';
import { ActivityReportReviewCard } from '@/components/features/provider/activity-report-review-card';
import { ActivityReportSection } from '@/components/features/activity-report-section';
import { WorkInvoiceCard } from '@/components/features/substitute/work-invoice-card';
import { type WorkInvoiceCardData } from '@/types/invoices';
import { ContractReviewSection } from '@/components/features/contract-review-section';
import { toast } from '@/template/lib/toast';
import i18n from '@/i18n';
import { trackDocumentUploadFailed, trackDocumentUploadSucceeded } from '@/lib/analytics';
import { getServiceDocumentUploadAvailability } from '@/lib/service-document-upload';
import { uploadChatAttachment, uploadFile } from '@/lib/upload-file';
import { type CompensationType } from '@/types/validators';
import { UnavailablePaymentMethodNotice } from '@/components/features/unavailable-payment-method-notice';
import { formatBerlinDate, formatBerlinDateWithOptions } from '@/lib/berlin-time';
import {
  type ProviderOwnedDuty,
  type ApplicationWithUser,
  type ApplicationUserProfile,
  type ServicePayment,
  type CurrentUser,
  type CurrentUserProfile,
  type DutyWorkInvoice,
  type DutyRating,
  type DocumentsSectionProps,
  type CompletedDutySectionProps,
  type PerformedDutySectionProps,
  type PaginatedMessagesResult,
} from '@/types/provider-service';

export const providerMobileChatButtonClassName = 'lg:hidden fixed bottom-20 right-6 h-14 w-14 rounded-full shadow-lg z-50 md:bottom-6 relative';

export const shouldShowOpenInvoiceTransferCard = (
  dutyStatus: ProviderOwnedDuty['status'] | undefined,
  payment: ServicePayment | null | undefined
): boolean => {
  const dutyFinalized =
    dutyStatus === 'pending_contract' ||
    dutyStatus === 'filled' ||
    dutyStatus === 'performed' ||
    dutyStatus === 'completed';
  return (
    dutyFinalized &&
    payment?.status === 'pending' &&
    payment.collectionMode === 'invoice_transfer' &&
    payment.hasIssuedInvoicePdf
  );
};

export const resolveCurrentAssigneeSummary = (
  duty: Pick<ProviderOwnedDuty, 'assigneeId' | 'agreedHourlyRate'>,
  applications: ApplicationWithUser[],
  assignee: ApplicationUserProfile | null | undefined
): { applicant: ApplicationUserProfile; compensation?: number } | null => {
  if (!duty.assigneeId || !assignee?._id || String(assignee._id) !== String(duty.assigneeId)) return null;
  const currentApplication = applications.find((application) => application.status === 'accepted' && String(application.applicantId) === String(duty.assigneeId));
  const compensation = duty.agreedHourlyRate ?? currentApplication?.proposedCompensation;
  return compensation === undefined ? { applicant: assignee } : { applicant: assignee, compensation };
};

const ProviderServiceDetailPage = () => {
  const { id } = Route.useParams();
  const dutyId = id as Id<'duties'>;
  // DA-475: Use efficient single-duty query instead of fetching all duties
  const duty = useQuery(api.app.duties.queries.getMyOwnedDutyById as any, { dutyId }) as ProviderOwnedDuty | null | undefined;

  const gating = useQuery(api.app.messages.queries.getThreadByService as any, {
    dutyId,
  });
  const chatAllowed = gating !== null && gating !== undefined;
  const chatEnabled = gating !== null; // enable typing during loading and when allowed

  // Applications list and actions
  const allApplications =
    (useQuery(api.app.applications.queries.listApplicationsForDutyWithUsers as any, {
      dutyId,
    }) as ApplicationWithUser[] | undefined) ?? [];

  // Filter out rejected applications for display
  const applications = React.useMemo(() => allApplications.filter((app) => app.status !== 'rejected'), [allApplications]);

  const setAppStatus = useMutation(api.app.applications.mutations.setApplicationStatus as any);

  const pagination = usePaginatedQuery(api.app.messages.queries.listMessagesByDuty as any, chatAllowed ? { dutyId, paginationOpts: { numItems: 20 } } : 'skip', { initialNumItems: chatAllowed ? 20 : 1 });
  const send = useMutation(api.app.messages.mutations.sendMessage as any);
  const markRead = useMutation(api.app.messages.mutations.markDutyChatRead as any);
  const unread = useQuery(api.app.messages.queries.getDutyChatUnreadCount as any, chatAllowed ? { dutyId } : 'skip');

  // Check for pending payment for this service
  const servicePayment = useQuery(api.app.billing.getPaymentForService as any, {
    serviceId: dutyId,
  }) as ServicePayment | null | undefined;
  const pollPaymentStatus = useAction(api.app.payments.webhookActions.pollPaymentStatus as any);
  const [dismissedPaidBannerPaymentId, setDismissedPaidBannerPaymentId] = React.useState<Id<'payments'> | null>(null);
  const hasCheckedRef = React.useRef(false);
  const isServicePaymentSucceeded = servicePayment?.status === 'succeeded';
  const showPaidBanner = isServicePaymentSucceeded && servicePayment?._id !== dismissedPaidBannerPaymentId;
  const showOpenInvoiceCard = shouldShowOpenInvoiceTransferCard(duty?.status, servicePayment);

  // In dev (no webhooks), trigger ONE status check if payment is pending
  // Then Convex reactivity handles all updates automatically
  React.useEffect(() => {
    // Only check if duty is pending_payment and payment status is still pending
    if (duty?.status !== 'pending_payment') return;
    if (!servicePayment?.molliePaymentId) return;
    if (servicePayment?.status !== 'pending') return;
    if (hasCheckedRef.current) return;

    // Trigger one-time check - Convex reactivity will update UI when status changes
    hasCheckedRef.current = true;
    pollPaymentStatus({ molliePaymentId: servicePayment.molliePaymentId }).catch(console.error);
  }, [duty?.status, servicePayment?.molliePaymentId, servicePayment?.status, pollPaymentStatus]);

  const [text, setText] = React.useState('');
  const createChatUploadUrl = useMutation(api.app.storage.mutations.createUploadUrl);
  const sendAttachment = useMutation(api.app.storage.mutations.sendAttachment);

  // Deletion request
  const requestDeletion = useMutation(api.app.duties.mutations.requestDutyDeletion as any);
  const [deletionDialogOpen, setDeletionDialogOpen] = React.useState(false);
  const [deletionReason, setDeletionReason] = React.useState('');
  const [deletionSubmitting, setDeletionSubmitting] = React.useState(false);

  // Compensation edit
  const updateCompensation = useMutation(api.app.duties.mutations.updateCompensation as any);
  const [compensationDialogOpen, setCompensationDialogOpen] = React.useState(false);
  const [editCompensationType, setEditCompensationType] = React.useState<CompensationType>('fixed');
  const [editMinPrice, setEditMinPrice] = React.useState('');
  const [editMaxPrice, setEditMaxPrice] = React.useState('');
  const [editHasNotfallschein, setEditHasNotfallschein] = React.useState(false);
  const [editNotfallscheinRate, setEditNotfallscheinRate] = React.useState('');
  const [compensationSubmitting, setCompensationSubmitting] = React.useState(false);

  // Current Convex user id for alignment (compare with message.senderId)
  const me = useQuery(api.app.users.queries.getMe as any, {}) as CurrentUser | null | undefined;
  const myUserId = me?._id ? String(me._id) : null;

  // Get user profile for MVZ status (DA-393: for commission rate calculation)
  const myProfile = useQuery(api.app.users.queries.getMyUserProfile as any, {}) as CurrentUserProfile | null | undefined;

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const onSend = async () => {
    if (!text.trim()) return;
    await send({ dutyId, content: text });
    setText('');
    await markRead({ dutyId });
  };

  // Prepare utilities and state BEFORE any early returns to keep hook order stable
  const fmt = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });

  // Booking modal state (declare before guard so hooks order is consistent)
  const [booking, setBooking] = React.useState<ApplicationWithUser | null>(null);
  const onConfirmClick = (app: ApplicationWithUser) => setBooking(app);

  // Reject handler
  const handleRejectApplication = async (applicationId: Id<'applications'>) => {
    try {
      await setAppStatus({
        applicationId,
        status: 'rejected',
      });
      toast.success(i18n.t('duties:providerServiceDetail.toasts.applicationRejected'));
    } catch (error) {
      console.error('Error rejecting application:', error);
      toast.error(i18n.t('duties:providerServiceDetail.toasts.applicationRejectFailed'));
    }
  };

  // Chat panel state for desktop/mobile
  const [chatOpen, setChatOpen] = React.useState(true);
  const [mobileChatOpen, setMobileChatOpen] = React.useState(false);
  const [docsExpanded, setDocsExpanded] = React.useState(true);

  if (duty === undefined) return <div className='max-w-6xl mx-auto px-4 lg:px-6 py-12 text-center text-sm text-muted-foreground'>Dienst wird geladen...</div>;
  if (duty === null) return <div className='max-w-6xl mx-auto px-4 lg:px-6 py-12 text-center text-sm text-muted-foreground'>Dienst nicht gefunden.</div>;
  const maxNotfallscheinRate = maxNotfallscheinbeteiligungRate(duty.serviceType);

  // const items = [...(pagination?.results || [])].reverse()
  const paginationState = pagination as unknown as PaginatedMessagesResult | undefined;
  const canLoadMore = paginationState?.status === 'CanLoadMore' && typeof paginationState?.loadMore === 'function';
  const showDiensttauschFormular = shouldShowDiensttauschFormular(duty.status);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadChatAttachment({
        dutyId,
        file,
        createUploadUrl: createChatUploadUrl,
        finalizeAttachment: sendAttachment,
        markRead,
        onMarkReadError: (error) => console.error('Failed to update chat read marker:', error),
      });
      toast.success(i18n.t('duties:providerServiceDetail.toasts.fileUploaded'));
    } catch (error) {
      console.error('File upload error:', error);
      toast.error(error instanceof Error ? error.message : i18n.t('duties:providerServiceDetail.toasts.fileUploadFailed'));
    } finally {
      e.target.value = '';
    }
  };

  const handlePickFile = () => fileInputRef.current?.click();

  const handleRequestDeletion = async () => {
    if (!deletionReason.trim()) {
      toast.error(i18n.t('duties:providerServiceDetail.toasts.deletionReasonRequired'));
      return;
    }
    setDeletionSubmitting(true);
    try {
      await requestDeletion({
        dutyId,
        reason: deletionReason,
      });
      toast.success(i18n.t('duties:providerServiceDetail.toasts.deletionRequested'));
      setDeletionDialogOpen(false);
      setDeletionReason('');
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : i18n.t('duties:providerServiceDetail.toasts.deletionRequestFailed'));
    } finally {
      setDeletionSubmitting(false);
    }
  };

  const handleOpenCompensationDialog = () => {
    // Initialize form with current values
    setEditCompensationType(duty?.compensationType || 'fixed');
    setEditMinPrice(duty?.minPrice?.toString() || '');
    setEditMaxPrice(duty?.maxPrice?.toString() || '');
    setEditHasNotfallschein(duty?.hasNotfallscheinbeteiligung || false);
    setEditNotfallscheinRate(duty?.notfallscheinbeteiligungRate?.toString() || '');
    setCompensationDialogOpen(true);
  };

  const handleUpdateCompensation = async () => {
    const minPrice = parseFloat(editMinPrice);
    const maxPrice = editCompensationType === 'range' ? parseFloat(editMaxPrice) : undefined;
    const notfallscheinRate = editHasNotfallschein ? parseFloat(editNotfallscheinRate) : undefined;

    // Validation
    if (isNaN(minPrice) || minPrice <= 0) {
      toast.error(i18n.t('duties:providerServiceDetail.toasts.invalidHourlyRate'));
      return;
    }

    if (editCompensationType === 'range' && (isNaN(maxPrice!) || maxPrice! <= 0 || maxPrice! < minPrice)) {
      toast.error(i18n.t('duties:providerServiceDetail.toasts.maxRateMustExceedMinRate'));
      return;
    }

    if (editHasNotfallschein && (isNaN(notfallscheinRate!) || notfallscheinRate! < 5 || notfallscheinRate! > maxNotfallscheinRate)) {
      toast.error(i18n.t('duties:providerServiceDetail.toasts.notfallscheinRateRange', { max: maxNotfallscheinRate }));
      return;
    }

    setCompensationSubmitting(true);
    try {
      await updateCompensation({
        dutyId,
        compensationType: editCompensationType,
        minPrice,
        ...(editCompensationType === 'range' && maxPrice !== undefined ? { maxPrice } : {}),
        hasNotfallscheinbeteiligung: editHasNotfallschein,
        ...(editHasNotfallschein && notfallscheinRate !== undefined ? { notfallscheinbeteiligungRate: notfallscheinRate } : {}),
      });
      toast.success(i18n.t('duties:providerServiceDetail.toasts.compensationUpdated'));
      setCompensationDialogOpen(false);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : i18n.t('duties:providerServiceDetail.toasts.compensationUpdateFailed'));
    } finally {
      setCompensationSubmitting(false);
    }
  };

  return (
    <div className='max-w-6xl mx-auto px-4 lg:px-6 py-6 space-y-6'>
      {/* Page Hero */}
      <PageHero
        title={duty.title}
        {...(duty.specialty !== undefined && { specialty: duty.specialty })}
        status={duty.status}
        serviceType={duty.serviceType}
        backHref='/provider/services'
        backLabel='Meine Dienste'
        onRequestDeletion={() => setDeletionDialogOpen(true)}
        canRequestDeletion={duty.status !== 'completed' && duty.status !== 'cancelled' && !duty.deletionRequested}
        {...(duty.deletionRequested !== undefined && { deletionRequested: duty.deletionRequested })}
        {...(duty.deletionRequestedAt !== undefined && { deletionRequestedAt: duty.deletionRequestedAt })}
        {...(duty.deletionReason !== undefined && { deletionReason: duty.deletionReason })}
        {...(duty.deletionRequestStatus !== undefined && { deletionRequestStatus: duty.deletionRequestStatus })}
        {...(duty.deletionRequestReviewedAt !== undefined && { deletionRequestReviewedAt: duty.deletionRequestReviewedAt })}
        {...(duty.deletionRequestReviewReason !== undefined && { deletionRequestReviewReason: duty.deletionRequestReviewReason })}
      />

      {/* Progress Stepper */}
      <ServiceProgressStepper currentStatus={duty.status} className='py-2' />

      {duty.status !== 'completed' && showPaidBanner && servicePayment && (
        <div className='rounded-md border border-success/20 bg-success/10 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2' role='alert'>
          <div className='text-sm text-success'>{i18n.t('duties:detail.paymentSuccess')}</div>
          <div className='flex items-center gap-2'>
            <InvoiceDownloadButton paymentId={servicePayment._id} variant='outline' size='sm' />
            <Button variant='ghost' size='sm' className='h-9 w-9 p-0' onClick={() => setDismissedPaidBannerPaymentId(servicePayment._id)} aria-label='Schließen'>
              <X className='h-4 w-4' />
            </Button>
          </div>
        </div>
      )}

      {showOpenInvoiceCard && servicePayment && (
        <div className='rounded-md border border-primary/20 bg-primary/5 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between' role='status'>
          <div>
            <div className='font-medium'>{i18n.t('common:bookingPayment.invoicePayment.openTitle')}</div>
            <div className='text-sm text-muted-foreground'>
              {i18n.t('common:bookingPayment.invoicePayment.openDescription')}
            </div>
          </div>
          <InvoiceDownloadButton paymentId={servicePayment._id} variant='outline' size='sm' />
        </div>
      )}

      {/* Main Content Grid — full width when chat is locked, 3-col when chat is available */}
      <div className={cn('grid grid-cols-1 gap-6', chatAllowed && chatOpen ? 'lg:grid-cols-3' : 'lg:grid-cols-[1fr_auto]')}>
        {/* Main Content */}
        <div className={cn('space-y-6', chatAllowed && chatOpen ? 'lg:col-span-2' : '')}>
          {/* Service Details Card */}
          {duty.status !== 'completed' && (
            <ServiceDetailsCard
              duty={{
                startDate: duty.startDate,
                endDate: duty.endDate,
                serviceType: duty.serviceType,
                location: duty.location,
              }}
              applicationCount={applications.length}
              showApplications={duty.status === 'open' || duty.status === 'pending_review' || duty.status === 'pending_payment'}
              collapsible={false}
            />
          )}

          <div>
            {duty.status === 'completed' ? (
              <CompletedDutySection dutyId={dutyId} duty={duty} applications={allApplications} fmt={fmt} />
            ) : duty.status === 'performed' ? (
              <PerformedDutySection dutyId={dutyId} duty={duty} applications={allApplications} fmt={fmt} />
            ) : duty.status === 'pending_contract' || duty.status === 'filled' ? (
              <ContractReviewSection dutyId={id} />
            ) : (
              <>
                <h2 className='text-lg font-semibold mb-4'>Bewerbungen</h2>
                <ActiveApplicationsSection duty={duty} applications={applications} onAccept={onConfirmClick} onReject={handleRejectApplication} />
              </>
            )}
          </div>

          {showDiensttauschFormular && <DiensttauschFormularCard dutyId={dutyId} />}
        </div>

        {/* Sidebar */}
        <div className='space-y-6'>
          {/* Compensation Summary Card - shown for non-completed duties with compensation data */}
          {duty.status !== 'completed' && duty.minPrice && (
            <CompensationSummaryCard
              duty={{
                ...(duty.compensationType !== undefined && { compensationType: duty.compensationType }),
                minPrice: duty.minPrice,
                ...(duty.maxPrice !== undefined && { maxPrice: duty.maxPrice }),
                ...(duty.hasNotfallscheinbeteiligung !== undefined && { hasNotfallscheinbeteiligung: duty.hasNotfallscheinbeteiligung }),
                ...(duty.notfallscheinbeteiligungRate !== undefined && { notfallscheinbeteiligungRate: duty.notfallscheinbeteiligungRate }),
                startDate: duty.startDate,
                endDate: duty.endDate,
              }}
              onEdit={handleOpenCompensationDialog}
              canEdit={duty.status === 'pending_review' || duty.status === 'open'}
            />
          )}

          {/* Desktop Chat Panel — only rendered as a full panel when chat is available */}
          <div className='hidden lg:block'>
            {chatAllowed ? (
              chatOpen ? (
                <div className='sticky top-4'>
                  <div className='border rounded-md bg-background'>
                    {/* Chat header with controls */}
                    <div className='border-b p-3 flex items-center justify-between bg-muted/30'>
                      <div className='flex items-center gap-2'>
                        <MessageSquare className='size-4' />
                        <span className='text-sm font-medium'>Chat</span>
                        {typeof unread === 'number' && unread > 0 && (
                          <Badge variant='secondary' className='h-5 min-w-5 px-1.5 text-xs'>
                            {unread}
                          </Badge>
                        )}
                      </div>

                      {/* Collapse button */}
                      <Button variant='ghost' size='icon' className='h-9 w-9' onClick={() => setChatOpen(false)} aria-label='Chat minimieren'>
                        <ChevronRight className='size-4' />
                      </Button>
                    </div>

                    {/* Chat content */}
                    <div className='h-[calc(100vh-12rem)] max-h-[500px] flex flex-col'>
                      <div className='flex-1 overflow-hidden'>
                        <Conversation className='h-full'>
                          <ConversationContent className='space-y-2 p-3'>
                            {canLoadMore && (
                              <div className='flex justify-center pb-2'>
                                <Button variant='secondary' size='sm' onClick={() => pagination?.loadMore?.(20)}>
                                  Mehr laden
                                </Button>
                              </div>
                            )}
                            <ErrorBoundary
                              onError={(err: Error) => {
                                console.debug('Chat disabled due to error:', err.message);
                              }}
                            >
                              <MessagesList serviceId={dutyId} currentUserId={myUserId ?? ''} enabled={!!chatAllowed} />
                            </ErrorBoundary>
                          </ConversationContent>
                        </Conversation>
                      </div>

                      <div className='border-t p-3 bg-muted/10'>
                        {duty.status === 'completed' ? (
                          <div className='p-2 text-center text-xs text-muted-foreground'>Chat ist archiviert</div>
                        ) : (
                          <>
                            <ConversationComposer value={text} onChange={setText} onSend={onSend} onPickFile={handlePickFile} disabled={!chatEnabled} placeholder='Nachricht schreiben' />
                            <input ref={fileInputRef} type='file' accept='image/*,.pdf' className='hidden' onChange={onFileChange} />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <MinimizedChatIndicator chatLocked={false} unreadCount={typeof unread === 'number' ? unread : 0} onExpand={() => setChatOpen(true)} />
              )
            ) : (
              /* Chat locked: compact inline indicator, no full panel */
              <MinimizedChatIndicator chatLocked onExpand={() => {}} />
            )}
          </div>
        </div>
      </div>

      {/* Collapsible Documents Section */}
      {duty.status !== 'completed' && (
        <Card className='overflow-hidden'>
          <button type='button' onClick={() => setDocsExpanded((prev) => !prev)} className='w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors' aria-expanded={docsExpanded} aria-controls='documents-section'>
            <div className='flex items-center gap-2'>
              <FileText className='size-5 text-muted-foreground' />
              <span className='font-semibold'>Dokumente</span>
            </div>
            <ChevronDown className={cn('size-5 text-muted-foreground transition-transform duration-200', docsExpanded && 'rotate-180')} />
          </button>

          <div id='documents-section' className={cn('transition-all duration-200 overflow-hidden', docsExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0')}>
            <div className='border-t p-4'>
              <DocumentsSection serviceId={dutyId} dutyStatus={duty.status} />
            </div>
          </div>
        </Card>
      )}

      {/* Mobile: Sheet overlay for chat */}
      <Sheet open={mobileChatOpen} onOpenChange={setMobileChatOpen}>
        <SheetContent side='bottom' className='h-[85vh] lg:hidden p-0'>
          <SheetHeader className='border-b p-4 bg-muted/30'>
            <div className='flex items-center justify-between'>
              <SheetTitle className='flex items-center gap-2'>
                <MessageSquare className='size-5' />
                Chat
              </SheetTitle>
              {chatAllowed && typeof unread === 'number' && unread > 0 && <Badge variant='secondary'>{unread} ungelesen</Badge>}
              {!chatAllowed && (
                <Badge variant='outline' className='flex items-center gap-1'>
                  <Lock className='size-3' /> Gesperrt
                </Badge>
              )}
            </div>
          </SheetHeader>

          <div className='h-full flex flex-col'>
            {!chatAllowed ? (
              <div className='flex-1 flex flex-col items-center justify-center text-center gap-3 p-6'>
                <Lock className='size-8 opacity-60' />
                <div className='text-base font-medium'>Chat wird nach Buchung verfügbar</div>
                <div className='text-sm text-muted-foreground'>Bestätigen Sie eine Bewerbung, um das Messaging zu starten.</div>
              </div>
            ) : (
              <>
                <div className='flex-1 overflow-hidden'>
                  <Conversation className='h-full'>
                    <ConversationContent className='space-y-2 p-4'>
                      {canLoadMore && (
                        <div className='flex justify-center pb-2'>
                          <Button variant='secondary' size='sm' onClick={() => pagination?.loadMore?.(20)}>
                            Mehr laden
                          </Button>
                        </div>
                      )}
                      <ErrorBoundary>
                        <MessagesList serviceId={dutyId} currentUserId={myUserId ?? ''} enabled={!!chatAllowed} />
                      </ErrorBoundary>
                    </ConversationContent>
                  </Conversation>
                </div>

                <div className='border-t p-4 bg-muted/10'>
                  {duty.status === 'completed' ? (
                    <div className='p-3 text-center text-sm text-muted-foreground'>Chat ist archiviert</div>
                  ) : (
                    <>
                      <ConversationComposer value={text} onChange={setText} onSend={onSend} onPickFile={handlePickFile} disabled={!chatEnabled} placeholder='Nachricht schreiben' />
                      <input ref={fileInputRef} type='file' accept='image/*,.pdf' className='hidden' onChange={onFileChange} />
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Mobile floating action button */}
      <Button variant='default' size='icon' className={providerMobileChatButtonClassName} onClick={() => setMobileChatOpen(true)} aria-label='Chat öffnen'>
        <MessageSquare className='size-6' />
        {chatAllowed && typeof unread === 'number' && unread > 0 && <div className='absolute -top-1 -right-1 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-xs font-bold'>{unread}</div>}
      </Button>

      {/* Payment Modal - DA-393: Redesigned - no substitute details until after booking */}
      {booking && (
        <BookingPaymentModal
          open={true}
          onOpenChange={(open) => !open && setBooking(null)}
          serviceId={dutyId}
          applicationId={booking._id}
          serviceTitle={duty.title}
          dutyStartDate={duty.startDate}
          dutyEndDate={duty.endDate}
          {...(duty.compensationType !== undefined && { compensationType: duty.compensationType })}
          {...(duty.minPrice !== undefined && { minPrice: duty.minPrice })}
          {...(duty.maxPrice !== undefined && { maxPrice: duty.maxPrice })}
          {...(booking.proposedCompensation !== undefined && { proposedCompensation: booking.proposedCompensation })}
          isMvz={myProfile?.isMVZ ?? myProfile?.isMvz ?? false}
          providerSepaBookingEnabled={myProfile?.providerSepaBookingEnabled ?? false}
          providerInvoicePaymentEnabled={myProfile?.providerInvoicePaymentEnabled ?? false}
        />
      )}

      {/* Deletion Request Dialog */}
      <Dialog open={deletionDialogOpen} onOpenChange={setDeletionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dienst zur Löschung anfordern</DialogTitle>
            <DialogDescription>Bitte geben Sie einen Grund für die Löschungsanfrage an. Ein Operator wird Ihre Anfrage überprüfen und den Dienst gegebenenfalls löschen.</DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='deletion-reason'>Grund für die Löschung</Label>
              <Textarea id='deletion-reason' placeholder='z.B. Dienst wurde bereits anderweitig besetzt, Fehlerhafte Daten, etc.' value={deletionReason} onChange={(e) => setDeletionReason(e.target.value)} rows={4} disabled={deletionSubmitting} />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setDeletionDialogOpen(false)} disabled={deletionSubmitting}>
              Abbrechen
            </Button>
            <Button variant='destructive' onClick={handleRequestDeletion} disabled={deletionSubmitting || !deletionReason.trim()}>
              {deletionSubmitting && <Icons.Loader className='mr-2 h-4 w-4 animate-spin' />}
              Löschung anfordern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compensation Edit Dialog */}
      <Dialog open={compensationDialogOpen} onOpenChange={setCompensationDialogOpen}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Vergütung bearbeiten</DialogTitle>
            <DialogDescription>Aktualisieren Sie die Vergütungsbedingungen für diesen Dienst. Änderungen sind nur vor der Zuweisung möglich.</DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            {/* Compensation Type */}
            <div className='space-y-2'>
              <Label htmlFor='compensation-type'>Vergütungsart</Label>
              <Select value={editCompensationType} onValueChange={(v: string | null) => setEditCompensationType(v as CompensationType)} disabled={compensationSubmitting}>
                <SelectTrigger id='compensation-type'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='fixed'>Fester Stundensatz</SelectItem>
                  <SelectItem value='range'>Preisspanne</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Min Price */}
            <div className='space-y-2'>
              <Label htmlFor='min-price'>{editCompensationType === 'fixed' ? 'Stundensatz' : 'Minimaler Stundensatz'} (€)</Label>
              <Input id='min-price' type='number' step='0.01' min='0' placeholder='z.B. 50.00' value={editMinPrice} onChange={(e) => setEditMinPrice(e.target.value)} disabled={compensationSubmitting} />
            </div>

            {/* Max Price (only for range) */}
            {editCompensationType === 'range' && (
              <div className='space-y-2'>
                <Label htmlFor='max-price'>Maximaler Stundensatz (€)</Label>
                <Input id='max-price' type='number' step='0.01' min='0' placeholder='z.B. 80.00' value={editMaxPrice} onChange={(e) => setEditMaxPrice(e.target.value)} disabled={compensationSubmitting} />
              </div>
            )}

            {/* Notfallscheinbeteiligung Toggle */}
            <div className='flex items-center justify-between py-2 border-t'>
              <div className='space-y-0.5'>
                <Label htmlFor='notfallschein-toggle'>Notfallscheinbeteiligung</Label>
                <div className='text-xs text-muted-foreground'>Zusätzliche Vergütung pro behandeltem Patient</div>
              </div>
              <Switch id='notfallschein-toggle' checked={editHasNotfallschein} onCheckedChange={setEditHasNotfallschein} disabled={compensationSubmitting} />
            </div>

            {/* Notfallscheinbeteiligung Rate (only if enabled) */}
            {editHasNotfallschein && (
              <div className='space-y-2'>
                <Label htmlFor='notfallschein-rate'>Betrag pro Patient (€)</Label>
                <Input id='notfallschein-rate' type='number' step='0.01' min='5' max={maxNotfallscheinRate} placeholder={`5.00 - ${maxNotfallscheinRate}.00`} value={editNotfallscheinRate} onChange={(e) => setEditNotfallscheinRate(e.target.value)} disabled={compensationSubmitting} />
                <div className='text-xs text-muted-foreground'>Muss zwischen 5 € und {maxNotfallscheinRate} € liegen</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setCompensationDialogOpen(false)} disabled={compensationSubmitting}>
              Abbrechen
            </Button>
            <Button onClick={handleUpdateCompensation} disabled={compensationSubmitting || !editMinPrice}>
              {compensationSubmitting && <Icons.Loader className='mr-2 h-4 w-4 animate-spin' />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const Route = createFileRoute('/_protected/provider/service/$id')({
  component: ProviderServiceDetailPage,
});

// Documents: simplified, Drive-like grid rebuilt from scratch
export const DocumentsSection: React.FC<DocumentsSectionProps> = ({ serviceId, dutyStatus }) => {
  const docs = (useQuery(api.app.documents.queries.listDocumentsByDuty as any, { dutyId: serviceId }) as any[] | undefined) ?? [];
  const serviceDocs = (useQuery(api.app.storage.queries.listServiceDocuments as any, { serviceId }) as any[] | undefined) ?? [];

  const createUploadUrl = useMutation(api.app.storage.mutations.createUploadUrl);
  const createDocs = useMutation(api.app.documents.mutations.createDocuments as any);

  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([]);
  const [viewMode, setViewMode] = React.useState<'cards' | 'list'>('cards');
  const [documentTypes, setDocumentTypes] = React.useState<Record<string, 'image' | 'schein' | 'other'>>({});
  const [previewingDocIndex, setPreviewingDocIndex] = React.useState<number | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const uploadAvailability = React.useMemo(() => getServiceDocumentUploadAvailability(dutyStatus), [dutyStatus]);

  // Clear staged files when uploads become unavailable
  React.useEffect(() => {
    if (!uploadAvailability.canUpload && uploadedFiles.length > 0) {
      setUploadedFiles([]);
      setDocumentTypes({});
    }
  }, [uploadAvailability.canUpload, uploadedFiles.length]);

  // Initialize document types when files are added
  React.useEffect(() => {
    const newTypes: Record<string, 'image' | 'schein' | 'other'> = { ...documentTypes };
    let hasChanges = false;
    uploadedFiles.forEach((file) => {
      if (!newTypes[file.name]) {
        newTypes[file.name] = 'other';
        hasChanges = true;
      }
    });
    if (hasChanges) {
      setDocumentTypes(newTypes);
    }
  }, [uploadedFiles, documentTypes]);

  const items = [...serviceDocs, ...docs];

  const formatFileSize = (bytes: number | undefined) => {
    if (typeof bytes !== 'number') return '';
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
  };

  // Manual upload handler
  const handleUpload = async () => {
    if (!uploadAvailability.canUpload) {
      toast.warning(uploadAvailability.message);
      return;
    }

    // Validate all files have document types
    const allFilesHaveType = uploadedFiles.every((file) => documentTypes[file.name]);
    if (!allFilesHaveType) {
      toast.warning(i18n.t('duties:providerServiceDetail.toasts.selectDocumentTypeForAllFiles'));
      return;
    }

    setUploading(true);
    const storageIds: Array<{ fileId: Id<'_storage'>; filename: string; documentType: 'image' | 'schein' | 'other' }> = [];
    const failedFiles: File[] = [];

    try {
      for (const file of uploadedFiles) {
        try {
          const url = await createUploadUrl({ dutyId: serviceId });
          const fileId = await uploadFile(url, file);
          storageIds.push({
            fileId,
            filename: file.name,
            documentType: documentTypes[file.name],
          });
        } catch (err) {
          failedFiles.push(file);
          console.error('Upload failed for file:', file.name, err);
          trackDocumentUploadFailed(err, {
            surface: 'service_documents',
            document_type: documentTypes[file.name],
            file_count: 1,
          });
          toast.error(i18n.t('duties:providerServiceDetail.toasts.fileUploadForDocumentFailed', { fileName: file.name }));
        }
      }

      if (storageIds.length > 0) {
        try {
          await createDocs({
            dutyId: serviceId,
            files: storageIds,
          });
          setUploadedFiles(failedFiles);
          setDocumentTypes((currentTypes) =>
            Object.fromEntries(failedFiles.map((file) => [file.name, currentTypes[file.name]])) as Record<string, 'image' | 'schein' | 'other'>
          );
          trackDocumentUploadSucceeded({
            surface: 'service_documents',
            file_count: storageIds.length,
          });
          toast.success(i18n.t('duties:providerServiceDetail.toasts.documentsUploaded', { count: storageIds.length }));
        } catch (e) {
          console.error('createDocs failed', e);
          trackDocumentUploadFailed(e, {
            surface: 'service_documents',
            file_count: storageIds.length,
          });
          toast.error(i18n.t('duties:providerServiceDetail.toasts.documentsSaveFailed'));
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const fileEntries = React.useMemo(() => {
    const out: any[] = [];
    for (const it of items) {
      if (Array.isArray(it.files)) {
        for (const f of it.files) {
          if (typeof f === 'string') {
            out.push({
              id: f,
              name: String(f),
              contentType: '',
              size: undefined,
              url: null,
            });
          } else {
            out.push({
              id: f.fileId || f._id || null,
              name: f.name || f.filename || f.title || String(f.fileId || f._id),
              contentType: f.contentType || f.type || '',
              size: f.size ?? f.bytes ?? undefined,
              url: f.url || null,
            });
          }
        }
      } else {
        // single file-like object
        if (it.fileId || it._id || it.name) {
          out.push({
            id: it.fileId || it._id || null,
            name: it.name || it.filename || String(it.fileId || it._id),
            contentType: it.contentType || '',
            size: it.size ?? undefined,
            url: it.url || null,
          });
        }
      }
    }
    return out;
  }, [items]);

  return (
    <div className='mt-8 space-y-6'>
      <div>
        <div className='flex items-center justify-between'>
          <div className='text-base font-medium'>Dokumente</div>
          <div className='flex items-center gap-2'>
            <Button variant={viewMode === 'cards' ? 'secondary' : 'ghost'} size='sm' onClick={() => setViewMode('cards')} aria-pressed={viewMode === 'cards'}>
              <LayoutGrid className='size-4' />
            </Button>
            <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size='sm' onClick={() => setViewMode('list')} aria-pressed={viewMode === 'list'}>
              <List className='size-4' />
            </Button>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          {uploadAvailability.canUpload ? (
            /* Controlled FileUpload: no auto-upload, user clicks Upload button after setting types */
            <FileUpload disabled={uploading} accept='image/*,.pdf' maxFiles={10} multiple value={uploadedFiles} onValueChange={setUploadedFiles}>
              <FileUploadDropzone>
                <div className='flex items-center gap-2'>
                  <div className='flex items-center justify-center rounded-full border p-2.5'>
                    <FileText className='size-5 text-muted-foreground' />
                  </div>
                  <div className='text-sm'>Dateien hierher ziehen oder</div>
                  <FileUploadTrigger render={<Button size='sm' variant='outline' />}>Dateien auswählen</FileUploadTrigger>
                </div>
              </FileUploadDropzone>

              <FileUploadList className='mt-3'>
                {uploadedFiles.map((file, idx) => (
                  <FileUploadItem key={idx} value={file} className='border rounded-md p-2 flex items-center gap-3'>
                    <FileUploadItemPreview />
                    <FileUploadItemMetadata />
                    <Select
                      disabled={uploading}
                      value={documentTypes[file.name] || 'other'}
                      onValueChange={(value) => {
                        if (value !== null) setDocumentTypes((prev) => ({ ...prev, [file.name]: value as 'image' | 'schein' | 'other' }));
                      }}
                    >
                      <SelectTrigger className='w-32'>
                        <SelectValue placeholder='Typ' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='image'>Bild</SelectItem>
                        <SelectItem value='schein'>Schein</SelectItem>
                        <SelectItem value='other'>Sonstiges</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className='ml-auto flex items-center gap-2'>
                      <FileUploadItemProgress variant='linear' />
                      <FileUploadItemDelete disabled={uploading} render={<Button variant='ghost' size='icon' className='size-9' />}>
                        <X />
                      </FileUploadItemDelete>
                    </div>
                  </FileUploadItem>
                ))}
              </FileUploadList>

              {/* Upload button - only show when there are files */}
              {uploadedFiles.length > 0 && (
                <div className='mt-3'>
                  <Button onClick={handleUpload} disabled={uploading || !uploadedFiles.every((file) => documentTypes[file.name])} className='w-full'>
                    {uploading ? 'Wird hochgeladen...' : `${uploadedFiles.length} ${uploadedFiles.length === 1 ? 'Dokument' : 'Dokumente'} hochladen`}
                  </Button>
                </div>
              )}
            </FileUpload>
          ) : (
            <div className='w-full rounded-md border bg-muted/5 p-4' data-testid='document-upload-unavailable'>
              <div className='flex gap-3'>
                <AlertTriangle className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                <div>
                  <div className='text-sm font-medium'>Dokumentenupload noch nicht verfügbar</div>
                  <div className='mt-1 text-sm text-muted-foreground'>{uploadAvailability.message}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='mt-4'>
        {items.length === 0 ? (
          <div className='border rounded-md p-4 bg-muted/5 text-sm text-muted-foreground'>Keine Dokumente vorhanden.</div>
        ) : (
          <div>
            {viewMode === 'cards' ? (
              <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4'>
                {fileEntries.map((f: any, index: number) => {
                  const name = f.name || 'Dokument';
                  const contentType = f.contentType || '';
                  const size = f.size;
                  const isImage = (f.contentType || '').startsWith('image/');
                  const isPdf = (f.contentType || '').includes('pdf');

                  return (
                    <button
                      key={String(f.id)}
                      type='button'
                      onClick={() => setPreviewingDocIndex(index)}
                      aria-label={`Dokument öffnen: ${name}`}
                      className='border rounded-lg overflow-hidden bg-background text-left hover:border-primary/40 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 group'
                    >
                      <div className='h-36 bg-muted/5 flex items-center justify-center relative'>
                        {isImage && f.url ? (
                          <>
                            <img src={f.url} alt={name} className='h-full w-full object-cover' />
                            <div className='absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center'>
                              <Eye className='size-6 text-white opacity-0 group-hover:opacity-100 transition-opacity' />
                            </div>
                          </>
                        ) : isPdf ? (
                          <div className='w-full h-full flex items-center justify-center bg-muted/30 relative'>
                            <div className='flex flex-col items-center gap-2 text-muted-foreground'>
                              <FileText className='size-10' />
                              <span className='text-xs font-semibold tracking-wider'>PDF</span>
                            </div>
                            <div className='absolute inset-0 bg-muted-foreground/0 group-hover:bg-muted-foreground/5 transition-colors flex items-center justify-center'>
                              <Eye className='size-6 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity' />
                            </div>
                          </div>
                        ) : (
                          <div className='w-full h-full flex items-center justify-center relative'>
                            <FileText className='size-12 text-muted-foreground' />
                            <div className='absolute inset-0 bg-black/0 group-hover:bg-muted/10 transition-colors flex items-center justify-center'>
                              <Eye className='size-6 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity' />
                            </div>
                          </div>
                        )}
                        {/* Status pairs color + icon + label (Color-Plus Rule); solid card bg keeps it legible over thumbnails */}
                        <div className='absolute top-2 right-2'>
                          {f.status === 'approved' && (
                            <span className='inline-flex items-center gap-1 rounded-full bg-card px-2 py-0.5 text-[10px] font-semibold text-success shadow-sm'>
                              <Icons.CheckCircle className='size-3' aria-hidden='true' />
                              Bestätigt
                            </span>
                          )}
                          {f.status === 'rejected' && (
                            <span className='inline-flex items-center gap-1 rounded-full bg-card px-2 py-0.5 text-[10px] font-semibold text-destructive shadow-sm'>
                              <Icons.XCircle className='size-3' aria-hidden='true' />
                              Abgelehnt
                            </span>
                          )}
                          {f.status === 'submitted' && (
                            <span className='inline-flex items-center gap-1 rounded-full bg-card px-2 py-0.5 text-[10px] font-semibold text-warning shadow-sm'>
                              <Clock className='size-3' aria-hidden='true' />
                              Eingereicht
                            </span>
                          )}
                        </div>
                      </div>
                      <div className='p-3'>
                        <div className='text-sm font-medium text-foreground truncate' title={name}>
                          {name}
                        </div>
                        <div className='mt-1 text-xs text-muted-foreground flex items-center justify-between'>
                          <span>{(contentType || '—').split('/').pop()}</span>
                          <span>{formatFileSize(size)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className='w-full'>
                <table className='w-full text-sm'>
                  <thead className='bg-muted/10 text-xs text-muted-foreground'>
                    <tr>
                      <th className='text-left px-3 py-2'>Dateiname</th>
                      <th className='text-left px-3 py-2'>Typ</th>
                      <th className='text-left px-3 py-2'>Größe</th>
                      <th className='text-left px-3 py-2'>Status</th>
                      <th className='text-left px-3 py-2'>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fileEntries.map((f: any, index: number) => (
                      <tr key={String(f.id)} className='border-t hover:bg-muted/30 cursor-pointer transition-colors' onClick={() => setPreviewingDocIndex(index)}>
                        <td className='px-3 py-2 truncate'>{f.name}</td>
                        <td className='px-3 py-2'>{(f.contentType || '—').split('/').pop()}</td>
                        <td className='px-3 py-2'>{formatFileSize(f.size)}</td>
                        <td className='px-3 py-2'>
                          {f.status === 'approved' && (
                            <Badge variant='outline' className='bg-success/10 text-success border-success/30'>
                              Bestätigt
                            </Badge>
                          )}
                          {f.status === 'rejected' && (
                            <Badge variant='outline' className='bg-destructive/10 text-destructive border-destructive/30'>
                              Abgelehnt
                            </Badge>
                          )}
                          {f.status === 'submitted' && (
                            <Badge variant='outline' className='bg-warning/10 text-warning border-warning/30'>
                              Eingereicht
                            </Badge>
                          )}
                        </td>
                        <td className='px-3 py-2'>
                          <Button
                            variant='ghost'
                            size='icon'
                            className='h-9 w-9'
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setPreviewingDocIndex(index);
                            }}
                            aria-label='Dokument ansehen'
                          >
                            <Eye className='size-4' />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {previewingDocIndex !== null && (
        <DocumentPreviewModal
          documents={fileEntries}
          currentIndex={previewingDocIndex}
          onClose={() => setPreviewingDocIndex(null)}
          onNavigate={(index) => setPreviewingDocIndex(index)}
          onDownload={(doc) => {
            if (doc.url) {
              window.open(doc.url, '_blank');
            }
          }}
          formatFileSize={formatFileSize}
        />
      )}
    </div>
  );
};

// Completed Duty Section - Read-only summary view
export const CompletedDutySection: React.FC<CompletedDutySectionProps> = ({ dutyId, duty, applications, fmt }) => {
  const applicationAssignee = applications.find(({ status, applicantId, applicant }) =>
    status === 'accepted' && applicantId === duty.assigneeId && applicant?._id === duty.assigneeId
  )?.applicant;
  const assignee = useQuery(api.app.applications.queries.getDutyAssigneeWithProfile, applicationAssignee ? 'skip' : { dutyId }) as ApplicationUserProfile | null | undefined;
  const assigneeSummary = resolveCurrentAssigneeSummary(duty, applications, applicationAssignee ?? assignee);
  const docs = (useQuery(api.app.documents.queries.listDocumentsByDuty as any, { dutyId }) as any[] | undefined) ?? [];

  const workInvoice = useQuery(api.app.workInvoices.getWorkInvoiceForDuty as any, {
    dutyId,
  }) as DutyWorkInvoice | null | undefined;

  // DA-54: Rating functionality
  const existingRating = useQuery(api.app.ratings.queries.getRatingForDuty as any, { dutyId }) as DutyRating | null | undefined;
  const rateSubstitute = useMutation(api.app.ratings.mutations.rateSubstitute as any);
  const [rating, setRating] = React.useState(0);
  const [submittingRating, setSubmittingRating] = React.useState(false);
  const ratingRef = React.useRef<HTMLDivElement>(null);

  // Auto-scroll to rating section from email deep link (DA-54)
  React.useEffect(() => {
    if (window.location.hash === '#rate' && duty?.status === 'completed') {
      // Check if substitute can be rated (assigned and not yet rated)
      if (duty.assigneeId && !existingRating) {
        // Scroll to rating section
        setTimeout(() => {
          ratingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Clear hash to avoid re-triggering
          window.history.replaceState(null, '', window.location.pathname);
        }, 500); // Delay to ensure DOM is ready
      }
    }
  }, [duty, existingRating]);

  const handleRatingSubmit = async () => {
    if (rating < 1 || rating > 5) {
      toast.error(i18n.t('duties:providerServiceDetail.toasts.ratingRangeInvalid'));
      return;
    }
    setSubmittingRating(true);
    try {
      await rateSubstitute({
        dutyId,
        rating,
      });
      toast.success(i18n.t('duties:providerServiceDetail.toasts.ratingSubmitted'));
      setRating(0);
    } catch (err) {
      console.error('Failed to submit rating:', err);
      const errorMessage = err instanceof Error ? err.message : i18n.t('duties:providerServiceDetail.toasts.ratingSubmitFailed');
      toast.error(errorMessage);
    } finally {
      setSubmittingRating(false);
    }
  };

  return (
    <div className='space-y-6'>
      {/* Completion summary — payout inline, no hero banner */}
      {workInvoice && (
        <div className='flex items-center justify-between rounded-lg border p-4'>
          <div className='text-sm text-muted-foreground'>Ausgezahlte Vergütung</div>
          <div className='text-lg font-semibold'>{fmt.format(workInvoice.amount / 100)}</div>
        </div>
      )}

      {/* Confirmed Substitute */}
      {assigneeSummary && (
        <ConfirmedSubstituteCard
          applicant={assigneeSummary.applicant}
          {...(assigneeSummary.compensation !== undefined && { compensation: assigneeSummary.compensation })}
          duty={{
            startDate: duty.startDate,
            endDate: duty.endDate,
            ...(duty.hasNotfallscheinbeteiligung !== undefined && { hasNotfallscheinbeteiligung: duty.hasNotfallscheinbeteiligung }),
            ...(duty.notfallscheinbeteiligungRate !== undefined && { notfallscheinbeteiligungRate: duty.notfallscheinbeteiligungRate }),
          }}
        />
      )}

      {duty.activityReport && !duty.activityReportConfirmedAt && (
        <ActivityReportReviewCard
          dutyId={dutyId}
          activityReport={duty.activityReport as any}
          isConfirmed={false}
          auditOnly
        />
      )}
      {duty.activityReport && duty.activityReportConfirmedAt && (
        <ActivityReportSection
          activityReport={duty.activityReport as any}
          isConfirmed
          confirmedAt={duty.activityReportConfirmedAt}
        />
      )}
      {!duty.activityReport && (
        <div className='rounded-md border border-warning/20 bg-warning/10 p-4' role='status'>
          <div className='text-sm font-medium text-warning'>{i18n.t('duties:activityReport.waitingForReport')}</div>
          <div className='text-xs text-warning'>{i18n.t('duties:activityReport.waitingForReportDesc')}</div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Left Column - Service & Representative Details */}
        <div className='lg:col-span-2 space-y-6'>
          {/* Service Summary */}
          <div className='border rounded-lg p-5 bg-card'>
            <div className='flex items-center gap-2 mb-4'>
              <Calendar className='size-5 text-primary' />
              <h3 className='text-base font-semibold'>Dienstübersicht</h3>
            </div>
            <div className='grid grid-cols-2 gap-x-6 gap-y-3 text-sm'>
              <div>
                <div className='text-xs text-muted-foreground mb-1'>Dienstbeginn</div>
                <div className='font-medium'>
                  {new Date(duty.startDate).toLocaleString('de-DE', {
                    timeZone: 'Europe/Berlin',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
              <div>
                <div className='text-xs text-muted-foreground mb-1'>Dienstende</div>
                <div className='font-medium'>
                  {new Date(duty.endDate).toLocaleString('de-DE', {
                    timeZone: 'Europe/Berlin',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
              <div>
                <div className='text-xs text-muted-foreground mb-1'>Dienstart</div>
                <div className='font-medium capitalize'>{duty.serviceType || '—'}</div>
              </div>
              <div>
                <div className='text-xs text-muted-foreground mb-1'>Fachrichtung</div>
                <div className='font-medium'>{duty.specialty || '—'}</div>
              </div>
              <div>
                <div className='text-xs text-muted-foreground mb-1'>Standort</div>
                <div className='font-medium'>
                  {duty.location?.city || '—'}
                  {duty.location?.state ? `, ${duty.location.state}` : ``}
                </div>
              </div>
            </div>
          </div>

          {/* Representative Info */}
          {assigneeSummary && (
            <div className='border rounded-lg p-5 bg-card'>
              <div className='flex items-center gap-2 mb-4'>
                <h3 className='text-base font-semibold'>Vertretungsarzt</h3>
              </div>
              <div className='space-y-3 text-sm'>
                {assigneeSummary.applicant.city && (
                  <div className='flex items-center justify-between py-2 border-b'>
                    <span className='text-muted-foreground'>Stadt</span>
                    <span className='font-medium flex items-center gap-1'>
                      <MapPin className='size-3' />
                      {assigneeSummary.applicant.city}
                    </span>
                  </div>
                )}
                <div className='flex items-center justify-between py-2 border-b'>
                  <span className='text-muted-foreground'>Bewertung</span>
                  <span className='font-medium'>{assigneeSummary.applicant.rating ?? '—'} / 5</span>
                </div>
                <div className='flex items-center justify-between py-2 border-b'>
                  <span className='text-muted-foreground'>Vertretungsdienste</span>
                  <span className='font-medium'>{assigneeSummary.applicant.completedServices ?? 0}</span>
                </div>
                <div className='flex items-center justify-between py-2'>
                  <span className='text-muted-foreground'>Vereinbarte Vergütung</span>
                  <span className='font-medium'>{assigneeSummary.compensation != null ? fmt.format(assigneeSummary.compensation) : '—'} / Stunde</span>
                </div>
              </div>
            </div>
          )}

          {/* Rating Section */}
          {duty.assigneeId && (
            <div ref={ratingRef} className='border rounded-lg p-5 bg-card'>
              <div className='flex items-center gap-2 mb-4'>
                <Star className='size-5 text-muted-foreground' />
                <h3 className='text-base font-semibold'>Ihre Bewertung</h3>
              </div>

              {existingRating ? (
                <div className='space-y-3'>
                  <div className='flex items-center gap-3'>
                    <div className='flex gap-0.5'>
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={cn('size-5', s <= existingRating.rating ? 'text-warning fill-warning' : 'text-muted-foreground/30')} aria-hidden='true' />
                      ))}
                    </div>
                    <span className='text-sm font-medium'>{existingRating.rating} / 5</span>
                  </div>
                  {existingRating.comment && (
                    <div className='p-3 bg-muted/30 rounded-md'>
                      <div className='text-sm'>{existingRating.comment}</div>
                    </div>
                  )}
                  <div className='text-xs text-muted-foreground'>
                    Bewertet am{' '}
                    {formatBerlinDateWithOptions(existingRating.createdAt, {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </div>
                </div>
              ) : (
                <div className='space-y-4'>
                  <div className='text-sm text-muted-foreground'>Wie zufrieden waren Sie mit dem Vertreter?</div>

                  <div className='space-y-3'>
                    <div className='flex flex-col items-center gap-3'>
                      <RatingInput value={rating} onChange={setRating} disabled={submittingRating} size='lg' />
                      {rating > 0 && (
                        <span className='text-sm font-medium'>
                          {rating} / 5 Stern{rating !== 1 ? 'e' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  <Button onClick={handleRatingSubmit} disabled={rating === 0 || submittingRating} className='w-full' size='default'>
                    {submittingRating ? 'Wird gesendet...' : 'Bewertung abgeben'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Documents Archive */}
          <div className='border rounded-lg p-5 bg-card'>
            <div className='flex items-center gap-2 mb-4'>
              <FileText className='size-5 text-primary' />
              <h3 className='text-base font-semibold'>Archivierte Dokumente</h3>
              <Badge variant='secondary' className='ml-auto'>
                {docs.length}
              </Badge>
            </div>
            {docs.length === 0 ? (
              <div className='text-sm text-muted-foreground text-center py-8'>Keine Dokumente vorhanden</div>
            ) : (
              <div className='space-y-2'>
                {docs.map((doc) => (
                  <div key={doc._id} className='flex items-center gap-3 p-3 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors'>
                    <div className='rounded bg-primary/10 p-2'>
                      <FileText className='size-4 text-primary' />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='text-sm font-medium truncate'>{doc.filename}</div>
                      <div className='text-xs text-muted-foreground'>
                        {formatBerlinDate(doc.submittedAt)}
                        {doc.patientCount !== undefined && <span className='ml-2'>• {doc.patientCount} Patienten</span>}
                      </div>
                    </div>
                    <Badge variant='outline' className='shrink-0 bg-success/10 text-success border-success/20'>
                      Bestätigt
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Payment Details */}
        <div className='space-y-6'>
          {/* DA-314: Work Invoice Card */}
          {workInvoice && <WorkInvoiceCard invoice={workInvoice as WorkInvoiceCardData} viewerRole='provider' />}

          {/* Info Card */}
          <div className='border rounded-lg p-4 bg-info/10 border-info/20'>
            <div className='flex items-start gap-3'>
              <AlertTriangle className='size-5 text-info shrink-0 mt-0.5' aria-hidden='true' />
              <div className='text-xs text-info'>
                <div className='font-semibold mb-1'>Archiviert</div>
                <div className='opacity-90'>Dieser Dienst ist abgeschlossen. Alle Informationen bleiben zur Einsicht verfügbar.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Performed Duty Section - Document Review & Payment
export const PerformedDutySection: React.FC<PerformedDutySectionProps> = ({ dutyId, duty, applications, fmt }) => {
  const loadedDocs = useQuery(api.app.documents.queries.listDocumentsByDuty as any, { dutyId }) as any[] | undefined;
  const docs = loadedDocs ?? [];
  const docsLoaded = loadedDocs !== undefined;

  const workInvoice = useQuery(api.app.workInvoices.getWorkInvoiceForDuty as any, {
    dutyId,
  }) as DutyWorkInvoice | null | undefined;

  const applicationAssignee = applications.find(({ status, applicantId, applicant }) =>
    status === 'accepted' && applicantId === duty.assigneeId && applicant?._id === duty.assigneeId
  )?.applicant;
  const assignee = useQuery(api.app.applications.queries.getDutyAssigneeWithProfile, applicationAssignee ? 'skip' : { dutyId }) as ApplicationUserProfile | null | undefined;
  const assigneeSummary = resolveCurrentAssigneeSummary(duty, applications, applicationAssignee ?? assignee);

  const confirmDoc = useMutation(api.app.documents.mutations.confirmDocument as any);
  const rejectDoc = useMutation(api.app.documents.mutations.rejectDocument as any);
  const createWorkPaymentAction = useAction(api.app.payments.workPaymentActions.createWorkPayment as any);
  const regenerateInvoice = useMutation(api.app.workInvoices.regenerateWorkInvoice as any);

  const [rejectingDocId, setRejectingDocId] = React.useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [paying, setPaying] = React.useState(false);
  const [regenerating, setRegenerating] = React.useState(false);
  const [pendingDocumentReview, setPendingDocumentReview] = React.useState<{ documentId: string; action: 'confirm' | 'reject' } | null>(null);
  const pendingDocumentReviewRef = React.useRef<{ documentId: string; action: 'confirm' | 'reject' } | null>(null);

  const setDocumentReviewPending = (pending: { documentId: string; action: 'confirm' | 'reject' } | null) => {
    pendingDocumentReviewRef.current = pending;
    setPendingDocumentReview(pending);
  };

  const handleConfirm = async (docId: string) => {
    if (pendingDocumentReviewRef.current) return;
    setDocumentReviewPending({ documentId: docId, action: 'confirm' });
    try {
      await confirmDoc({ documentId: docId });
      toast.success(i18n.t('duties:providerServiceDetail.toasts.documentConfirmed'));
    } catch (err) {
      console.error('Failed to confirm document:', err);
      toast.error(i18n.t('duties:providerServiceDetail.toasts.confirmDocumentFailed'));
    } finally {
      setDocumentReviewPending(null);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectingDocId || !rejectionReason.trim()) return;
    if (pendingDocumentReviewRef.current) return;
    const documentId = rejectingDocId;
    setDocumentReviewPending({ documentId, action: 'reject' });
    try {
      await rejectDoc({
        documentId,
        reason: rejectionReason,
      });
      toast.success(i18n.t('duties:providerServiceDetail.toasts.documentRejected'));
      setRejectingDocId(null);
      setRejectionReason('');
    } catch (err) {
      console.error('Failed to reject document:', err);
      toast.error(i18n.t('duties:providerServiceDetail.toasts.rejectDocumentFailed'));
    } finally {
      setDocumentReviewPending(null);
    }
  };

  const handlePayInvoice = async () => {
    if (!workInvoice) return;
    setPaying(true);
    try {
      // DA-315: Call real Mollie payment creation action
      const result = (await createWorkPaymentAction({ workInvoiceId: workInvoice._id })) as { checkoutUrl?: string } | null;

      if (!result || !result.checkoutUrl) {
        throw new Error('Checkout-URL konnte nicht erstellt werden');
      }

      // Redirect to Mollie checkout
      window.location.href = result.checkoutUrl;
    } catch (err: any) {
      console.error('Failed to create payment:', err);

      // Handle specific error cases
      if (err?.message?.includes('nicht abgeschlossen') || err?.message?.includes('not completed')) {
        toast.error(i18n.t('duties:providerServiceDetail.toasts.substituteMollieNotConnected'));
      } else if (err?.message?.includes('already paid')) {
        toast.error(i18n.t('duties:providerServiceDetail.toasts.invoiceAlreadyPaid'));
      } else {
        toast.error(err?.message || i18n.t('duties:providerServiceDetail.toasts.paymentInitiationFailed'));
      }

      setPaying(false);
    }
  };

  const handleRegenerateInvoice = async () => {
    setRegenerating(true);
    try {
      await regenerateInvoice({ dutyId });
      toast.success(i18n.t('duties:providerServiceDetail.toasts.invoiceGenerationStarted'));
      // The invoice should appear automatically via Convex reactivity
    } catch (err) {
      console.error('Failed to regenerate invoice:', err);
      toast.error(i18n.t('duties:providerServiceDetail.toasts.invoiceCreateFailed'));
    } finally {
      setRegenerating(false);
    }
  };

  const allDocsConfirmed = (docs.length > 0 || Boolean(duty.documentRequirementWaivedAt)) && docs.every((d) => d.status === 'approved');
  const reportConfirmed = Boolean(duty.activityReport && duty.activityReportConfirmedAt);

  return (
    <div className='space-y-4'>
      <div className='text-base font-medium'>{i18n.t('duties:detail.serviceCompleted')}</div>

      {/* Confirmed Substitute */}
      {assigneeSummary && (
        <ConfirmedSubstituteCard
          applicant={assigneeSummary.applicant}
          {...(assigneeSummary.compensation !== undefined && { compensation: assigneeSummary.compensation })}
          duty={{
            startDate: duty.startDate,
            endDate: duty.endDate,
            ...(duty.hasNotfallscheinbeteiligung !== undefined && { hasNotfallscheinbeteiligung: duty.hasNotfallscheinbeteiligung }),
            ...(duty.notfallscheinbeteiligungRate !== undefined && { notfallscheinbeteiligungRate: duty.notfallscheinbeteiligungRate }),
          }}
        />
      )}

      {/* Activity Report Review - DA-383 */}
      {/* Activity Report - Show review card if pending, display section if confirmed */}
      {duty.activityReport && !duty.activityReportConfirmedAt && (
        <ActivityReportReviewCard
          dutyId={dutyId}
          activityReport={duty.activityReport as any}
          {...(duty.hasNotfallscheinbeteiligung !== undefined && { hasNotfallscheinbeteiligung: duty.hasNotfallscheinbeteiligung })}
          {...(duty.notfallscheinbeteiligungRate !== undefined && { notfallscheinbeteiligungRate: duty.notfallscheinbeteiligungRate })}
          isConfirmed={false}
          auditOnly={duty.status === 'completed'}
          {...(docsLoaded && { uploadedDocuments: docs.length })}
          {...(duty.documentsReminderSentAt !== undefined && { documentsReminderSentAt: duty.documentsReminderSentAt })}
        />
      )}
      {duty.activityReport && duty.activityReportConfirmedAt && (
        <ActivityReportSection
          activityReport={duty.activityReport as any}
          {...(duty.hasNotfallscheinbeteiligung !== undefined && { hasNotfallscheinbeteiligung: duty.hasNotfallscheinbeteiligung })}
          {...(duty.notfallscheinbeteiligungRate !== undefined && { notfallscheinbeteiligungRate: duty.notfallscheinbeteiligungRate })}
          isConfirmed={true}
          confirmedAt={duty.activityReportConfirmedAt}
        />
      )}

      {/* Status Banner */}
      {!reportConfirmed && !duty.activityReport ? (
        <div className='rounded-md border border-warning/20 bg-warning/10 p-4' role='alert'>
          <div className='flex items-start gap-2'>
            <AlertTriangle className='size-5 text-warning' aria-hidden='true' />
            <div className='space-y-1 flex-1'>
              <div className='text-sm font-medium text-warning'>{i18n.t('duties:activityReport.waitingForReport')}</div>
              <div className='text-xs text-warning'>{i18n.t('duties:activityReport.waitingForReportDesc')}</div>
            </div>
          </div>
        </div>
      ) : reportConfirmed && !allDocsConfirmed ? (
        <div className='rounded-md border border-info/20 bg-info/10 p-4' role='alert'>
          <div className='flex items-start gap-2'>
            <FileText className='size-5 text-info mt-0.5' aria-hidden='true' />
            <div className='space-y-1 flex-1'>
              <div className='text-sm font-medium text-info'>{i18n.t('duties:detail.servicePerformed')}</div>
              <div className='text-xs text-info'>Bitte überprüfen Sie die hochgeladenen Dokumente des Vertreters. Nach der Bestätigung wird eine Rechnung erstellt.</div>
            </div>
          </div>
        </div>
      ) : reportConfirmed && workInvoice === null ? (
        <div className='rounded-md border border-warning/20 bg-warning/10 p-4' role='alert'>
          <div className='flex items-start gap-3'>
            <AlertTriangle className='size-5 text-warning mt-0.5 shrink-0' aria-hidden='true' />
            <div className='space-y-3 flex-1'>
              <div>
                <div className='text-sm font-medium text-warning'>{i18n.t('duties:detail.invoiceGenerating')}</div>
                <div className='text-xs text-warning mt-1'>{i18n.t('duties:detail.allDocumentsConfirmed')}</div>
              </div>
              <div className='pt-2 border-t border-warning/30'>
                <div className='text-xs text-warning mb-2'>{i18n.t('duties:detail.invoiceManualCreate')}</div>
                <Button size='sm' variant='outline' onClick={handleRegenerateInvoice} disabled={regenerating} className='border-warning text-warning hover:bg-warning/10'>
                  {regenerating ? i18n.t('duties:detail.invoiceGenerating') : i18n.t('duties:detail.invoiceCreateNow')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Documents to Review */}
      <div className='space-y-3'>
        <div className='text-sm font-medium'>Hochgeladene Dokumente</div>

        {docs.length === 0 ? (
          <div className='text-sm text-muted-foreground border rounded-md p-4 bg-muted/5'>Der Vertreter hat noch keine Dokumente hochgeladen.</div>
        ) : (
          docs.map((doc) => {
            const currentPendingReview = pendingDocumentReview;
            let pendingAction: 'confirm' | 'reject' | null = null;
            if (currentPendingReview !== null && currentPendingReview.documentId === doc._id) {
              pendingAction = currentPendingReview.action;
            }

            return (
              <div key={doc._id} className='border rounded-md p-3 space-y-3'>
                <div className='flex items-start justify-between gap-3'>
                  <div className='flex items-start gap-3 flex-1'>
                    <FileText className='size-5 text-muted-foreground mt-0.5' />
                    <div className='space-y-1 flex-1'>
                      <div className='flex items-center gap-2'>
                        <div className='text-sm font-medium'>{doc.filename}</div>
                        {doc.status === 'approved' && (
                          <Badge variant='outline' className='bg-success/20 text-success border-success/30'>
                            Bestätigt
                          </Badge>
                        )}
                        {doc.status === 'rejected' && (
                          <Badge variant='outline' className='bg-destructive/20 text-destructive border-destructive/30'>
                            Abgelehnt
                          </Badge>
                        )}
                        {doc.status === 'submitted' && (
                          <Badge variant='outline' className='bg-warning/20 text-warning border-warning/30'>
                            Ausstehend
                          </Badge>
                        )}
                      </div>
                      <div className='text-xs text-muted-foreground'>Hochgeladen am {formatBerlinDate(doc.submittedAt)}</div>
                      {doc.patientCount !== undefined && (
                        <div className='text-xs text-muted-foreground'>
                          Patientenanzahl: {doc.patientCount} • Notfallscheinbeteiligung: {fmt.format((doc.notfallscheinbeteiligungAmount || 0) / 100)}
                        </div>
                      )}
                      {doc.status === 'rejected' && doc.rejectionReason && <div className='text-xs text-destructive mt-2 p-2 bg-destructive/10 rounded border border-destructive/20'>Ablehnungsgrund: {doc.rejectionReason}</div>}
                    </div>
                  </div>

                  {doc.status === 'submitted' && (
                    <div className='flex items-center gap-2 shrink-0'>
                      <Button size='sm' variant='outline' onClick={() => handleConfirm(doc._id)} disabled={pendingDocumentReview !== null}>
                        {pendingAction === 'confirm' ? i18n.t('common:actions.saving') : 'Bestätigen'}
                      </Button>
                      <Button size='sm' variant='outline' onClick={() => setRejectingDocId(doc._id)} disabled={pendingDocumentReview !== null}>
                        Ablehnen
                      </Button>
                    </div>
                  )}
                </div>

                {/* Rejection Form */}
                {rejectingDocId === doc._id && (
                  <div className='border-t pt-3 space-y-2'>
                    <div className='text-xs font-medium'>Grund für Ablehnung</div>
                    <Textarea placeholder='Bitte beschreiben Sie, warum dieses Dokument abgelehnt wird...' value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} rows={3} />
                    <div className='flex gap-2'>
                      <Button size='sm' onClick={handleRejectSubmit} disabled={!rejectionReason.trim() || pendingDocumentReview !== null}>
                        {pendingAction === 'reject' ? i18n.t('common:actions.saving') : 'Ablehnung bestätigen'}
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        disabled={pendingDocumentReview !== null}
                        onClick={() => {
                          setRejectingDocId(null);
                          setRejectionReason('');
                        }}
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Invoice Section */}
      {reportConfirmed && allDocsConfirmed && workInvoice && (
        <div className='border-2 border-success/30 bg-success/10 rounded-lg p-5 space-y-4'>
          <div className='flex items-start gap-3'>
            <div className='rounded-full bg-success p-2'>
              <FileText className='size-5 text-white' aria-hidden='true' />
            </div>
            <div className='space-y-4 flex-1'>
              <div>
                <h3 className='text-base font-semibold text-success'>Rechnung erstellt — Bereit zur Abrechnung</h3>
                <div className='text-sm text-success mt-1'>Alle Dokumente wurden bestätigt. Bitte bezahlen Sie die Rechnung, um den Dienst abzuschließen.</div>
              </div>

              {/* DA-314: Work Invoice Card */}
              <WorkInvoiceCard invoice={workInvoice as WorkInvoiceCardData} viewerRole='provider' />

              {/* Payment Status - Awaiting Confirmation (Bank Transfer Only) */}
              {workInvoice.paymentMethod === 'bank_transfer' && workInvoice.status === 'issued' && !workInvoice.manualPaymentConfirmedAt && (
                <div className='mt-4 p-4 rounded-md bg-warning-light border border-warning-border'>
                  <div className='flex items-start gap-3'>
                    <Clock className='h-5 w-5 text-warning-foreground shrink-0 mt-0.5' />
                    <div className='flex-1'>
                      <div className='text-sm font-semibold text-warning-foreground'>Warte auf Zahlungsbestätigung</div>
                      <div className='text-xs text-warning-foreground opacity-80 mt-1'>Der Vertreter muss bestätigen, dass er die Überweisung auf sein Konto erhalten hat. Dies erfolgt automatisch nach dem Zahlungseingang.</div>
                      {workInvoice.issuedAt && (
                        <div className='text-xs text-warning-foreground opacity-70 mt-2'>
                          Rechnung ausgestellt am{' '}
                          {formatBerlinDateWithOptions(workInvoice.issuedAt, {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Section - Bank Transfer or Mollie */}
              <div className='mt-5 pt-4 border-t border-success/30'>
                {workInvoice.paymentMethod === 'bank_transfer' ? (
                  <ProviderBankTransferInstructions workInvoiceId={workInvoice._id} amount={workInvoice.amount} issuedAt={workInvoice.issuedAt} />
                ) : workInvoice.status === 'paid' ? (
                  <div className='text-center p-4 bg-success/20 border-2 border-success/40 rounded-lg'>
                    <h3 className='text-base font-semibold text-success'>Rechnung bezahlt</h3>
                    <div className='text-sm text-success mt-1'>Der Dienst ist abgeschlossen.</div>
                  </div>
                ) : workInvoice.paymentMethod === 'mollie_connect' ? (
                  <div className='space-y-3'>
                    <div className='text-sm font-medium text-success text-center'>Klicken Sie unten, um die Rechnung zu bezahlen und den Dienst abzuschließen:</div>
                    <Button className='w-full bg-success hover:bg-success/90' size='lg' onClick={handlePayInvoice} disabled={paying}>
                      {paying ? 'Zahlung wird verarbeitet...' : 'Rechnung bezahlen & Dienst abschließen'}
                    </Button>
                  </div>
                ) : (
                  <UnavailablePaymentMethodNotice />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Diensttauschformular Download Card Component
const DiensttauschFormularCard: React.FC<{ dutyId: Id<'duties'> }> = ({ dutyId }) => {
  const formular = useQuery(api.app.diensttauschFormulareQueries.getFormularForDownload as any, {
    dutyId,
  }) as { pdfUrl?: string; status?: string; generatedAt?: number } | null | undefined;

  const manualGenerate = useAction(api.app.diensttauschPDF.manuallyGenerateFormular as any);
  const [generating, setGenerating] = React.useState(false);

  const handleDownload = () => {
    if (formular?.pdfUrl) {
      window.open(formular.pdfUrl, '_blank');
    }
  };

  const handleManualGenerate = async () => {
    setGenerating(true);
    try {
      const result = await manualGenerate({ dutyId });
      if (result?.success) {
        toast.success(result.message);
      } else if (result?.message) {
        toast.warning(result.message);
      } else {
        toast.error(i18n.t('duties:providerServiceDetail.toasts.formGenerationFailed'));
      }
    } catch (err) {
      console.error('Failed to generate formular:', err);
      toast.error(i18n.t('duties:providerServiceDetail.toasts.formGenerationRetryFailed'));
    } finally {
      setGenerating(false);
    }
  };

  if (!formular) {
    return (
      <div data-testid='diensttausch-formular-section' className='border rounded-lg p-4 bg-muted/5'>
        <div className='flex items-center gap-2 mb-3'>
          <FileText className='size-5 text-muted-foreground' />
          <h3 className='text-sm font-semibold text-muted-foreground'>Diensttauschformular</h3>
        </div>
        <div className='text-xs text-muted-foreground mb-3'>Das Formular wird automatisch generiert, sobald beide Parteien den Vertrag akzeptiert haben. Falls es nicht erscheint, können Sie es manuell erstellen.</div>
        <Button onClick={handleManualGenerate} disabled={generating} size='sm' variant='outline' className='w-full'>
          {generating ? 'Wird generiert...' : 'Formular jetzt generieren'}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid='diensttausch-formular-section' className='border rounded-lg p-5 bg-card'>
      <div className='flex items-center gap-2 mb-4'>
        <FileText className='size-5 text-primary' />
        <h3 className='text-base font-semibold'>Diensttauschformular</h3>
      </div>

      <div className='space-y-3'>
        <div className='text-sm text-muted-foreground'>Automatisch generiertes Formular für die Einreichung bei Ihrer KV zur Abrechnung des Diensttausches.</div>

        <div className='p-3 rounded-md bg-muted/30 border'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <FileText className='size-4 text-primary' />
              <div>
                <div className='text-sm font-medium'>Diensttauschformular.pdf</div>
                <div className='text-xs text-muted-foreground'>
                  {formular.generatedAt && (
                    <>
                      Erstellt am{' '}
                      {formatBerlinDateWithOptions(formular.generatedAt, {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Button onClick={handleDownload} className='w-full' variant='outline' size='default'>
          <FileText className='size-4 mr-2' />
          PDF herunterladen
        </Button>

        <div className='text-xs text-info p-3 bg-info/10 rounded-md border border-info/20'>
          <div className='font-medium mb-1'>Hinweis:</div>
          <div>Bitte drucken Sie das Formular aus, unterzeichnen Sie es und fügen Sie Ihren Arztstempel hinzu. Anschließend reichen Sie es bei Ihrer KV zur Abrechnung ein.</div>
        </div>
      </div>
    </div>
  );
};
